import { useCallback, useEffect, useMemo, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import { ArrowRight as ArrowRightIcon } from '@phosphor-icons/react/dist/ssr/ArrowRight'
import { X as XIcon } from '@phosphor-icons/react/dist/ssr/X'
import { useList, useOne } from '@refinedev/core'
import { Link as RouterLink } from 'react-router-dom'

import type { CabinetServiceStatusRow, CabinetServiceStatusSummary } from '@/types/cabinet-health'
import { paths } from '@/paths'
import { CABINET_SERVICE_STATUS, cabinetDisplayName } from '@/lib/cabinet-health'
import { PLATFORM_LIVE_CHANNELS } from '@/lib/platform-live-channels'
import { useHealthAccess } from '@/hooks/use-health-access'
import { useLiveRefetch } from '@/hooks/use-live-refetch'
import { usePathname } from '@/hooks/use-pathname'
import { useWorkspaces } from '@/hooks/use-workspaces'
import { healthTabHref } from '@/components/dashboard/health/health-tabs'

const DISMISSED_SIGNATURE_KEY = 'active-alerts-banner-dismissed-v3'
const SERVICE_STATUS_RESOURCE = 'cabinets/service-status'

interface BannerSnapshot {
	operatorId: number
	outOfService: number
	withWarnings: number
	outageIds: number[]
	alertIds: number[]
}

function readDismissedSignature(): string | null {
	try {
		return sessionStorage.getItem(DISMISSED_SIGNATURE_KEY)
	} catch {
		return null
	}
}

function writeDismissedSignature(signature: string) {
	try {
		sessionStorage.setItem(DISMISSED_SIGNATURE_KEY, signature)
	} catch {
		// sessionStorage unavailable
	}
}

function clearDismissedSignature() {
	try {
		sessionStorage.removeItem(DISMISSED_SIGNATURE_KEY)
	} catch {
		// sessionStorage unavailable
	}
}

function sortedIds(ids: Iterable<number>): number[] {
	return [...ids].sort((a, b) => a - b)
}

function parseIdList(value: string): number[] | null {
	if (!value) return []
	const ids = value.split(',').map(Number)
	return ids.every((id) => Number.isInteger(id)) ? ids : null
}

function formatSnapshot(snapshot: BannerSnapshot): string {
	return [
		snapshot.operatorId,
		snapshot.outOfService,
		snapshot.withWarnings,
		snapshot.outageIds.join(','),
		snapshot.alertIds.join(','),
	].join(':')
}

function parseSnapshot(signature: string): BannerSnapshot | null {
	if (!signature) return null
	const [operatorId, outOfService, withWarnings, outageIds = '', alertIds = ''] = signature.split(':')
	const parsedOutages = parseIdList(outageIds)
	const parsedAlerts = parseIdList(alertIds)
	const op = Number(operatorId)
	const oos = Number(outOfService)
	const warnings = Number(withWarnings)
	if (!Number.isInteger(op) || !Number.isInteger(oos) || !Number.isInteger(warnings) || !parsedOutages || !parsedAlerts) {
		return null
	}
	return { operatorId: op, outOfService: oos, withWarnings: warnings, outageIds: parsedOutages, alertIds: parsedAlerts }
}

/** True while nothing new has opened since the user dismissed this operator's banner. */
function dismissalStillApplies(dismissedSignature: string, current: BannerSnapshot): boolean {
	const dismissed = parseSnapshot(dismissedSignature)
	if (!dismissed || dismissed.operatorId !== current.operatorId) return false
	if (current.outOfService > dismissed.outOfService || current.withWarnings > dismissed.withWarnings) return false
	const dismissedOutages = new Set(dismissed.outageIds)
	const dismissedAlerts = new Set(dismissed.alertIds)
	if (current.outageIds.some((id) => !dismissedOutages.has(id))) return false
	return !current.alertIds.some((id) => !dismissedAlerts.has(id))
}

function bannerSnapshot(
	operatorId: number,
	cabinets: CabinetServiceStatusRow[],
	summary?: CabinetServiceStatusSummary,
): BannerSnapshot {
	return {
		operatorId,
		outOfService: Number(summary?.out_of_service ?? cabinets.length),
		withWarnings: Number(summary?.with_warnings ?? 0),
		outageIds: sortedIds(cabinets.map((cabinet) => cabinet.cabinet_service_outage?.id ?? cabinet.id)),
		alertIds: sortedIds(cabinets.flatMap((cabinet) => (cabinet.alert_events ?? []).map((alert) => alert.id))),
	}
}

function bannerCopy(cabinets: CabinetServiceStatusRow[]): { message: string; linkLabel: string } {
	if (cabinets.length === 1) {
		return { message: `${cabinetDisplayName(cabinets[0])} is out of service`, linkLabel: 'View cabinet' }
	}
	return {
		message: `${cabinets.length} cabinets are out of service`,
		linkLabel: 'View cabinets',
	}
}

export function ActiveAlertsBanner() {
	const { currentOperator, isGlobalOperator, isAllOperator, isLoading: isWorkspaceLoading } = useWorkspaces()
	const pathname = usePathname()
	const [dismissedSignature, setDismissedSignature] = useState<string | null>(readDismissedSignature)
	const [isScrolled, setIsScrolled] = useState(false)
	const hasHealthAccess = useHealthAccess()

	const canFetch =
		hasHealthAccess && Boolean(currentOperator?.id) && !isGlobalOperator && !isAllOperator && !isWorkspaceLoading
	const isOnHealthPage = pathname === paths.dashboard.health

	const { data, refetch } = useList<CabinetServiceStatusRow>({
		resource: SERVICE_STATUS_RESOURCE,
		pagination: { current: 1, pageSize: 50 },
		meta: { query: { status: CABINET_SERVICE_STATUS.OutOfService } },
		errorNotification: false,
		queryOptions: {
			enabled: canFetch,
			retry: false,
		},
	})

	const { data: summaryData, isFetched: isSummaryFetched, refetch: refetchSummary } = useOne<CabinetServiceStatusSummary>({
		resource: SERVICE_STATUS_RESOURCE,
		id: 'summary',
		errorNotification: false,
		queryOptions: { enabled: canFetch, retry: false },
	})

	const clearDismiss = useCallback(() => {
		setDismissedSignature(null)
		clearDismissedSignature()
	}, [])

	useLiveRefetch({
		channel: PLATFORM_LIVE_CHANNELS.HEALTH_ALERTS,
		enabled: canFetch,
		onLiveEvent: (event) => {
			if (event.type === 'created') clearDismiss()
		},
		refetch: () => {
			void refetch()
			void refetchSummary()
		},
	})

	useEffect(() => {
		if (canFetch) {
			void refetch()
			void refetchSummary()
		}
	}, [canFetch, currentOperator?.id, refetch, refetchSummary])

	useEffect(() => {
		const updateScrolled = () => {
			setIsScrolled(window.scrollY > 0)
		}
		updateScrolled()
		window.addEventListener('scroll', updateScrolled, { passive: true })
		return () => {
			window.removeEventListener('scroll', updateScrolled)
		}
	}, [])

	const cabinets = useMemo(
		() => (data?.data ?? []).filter((cabinet) => cabinet.status === CABINET_SERVICE_STATUS.OutOfService),
		[data?.data]
	)
	const snapshot = currentOperator?.id && cabinets.length && isSummaryFetched
		? bannerSnapshot(currentOperator.id, cabinets, summaryData?.data)
		: null
	const signature = snapshot ? formatSnapshot(snapshot) : null
	const dismissedForOperator = parseSnapshot(dismissedSignature ?? '')?.operatorId === currentOperator?.id
	// Hold a dismiss for this operator until the summary arrives, so the banner does not flash.
	const isDismissed = Boolean(
		dismissedSignature && dismissedForOperator && (!snapshot || dismissalStillApplies(dismissedSignature, snapshot)),
	)

	const handleDismiss = useCallback(() => {
		if (!signature) return
		setDismissedSignature(signature)
		writeDismissedSignature(signature)
	}, [signature])

	if (!canFetch || isOnHealthPage || cabinets.length === 0 || !snapshot || isDismissed) {
		return null
	}

	const { message, linkLabel } = bannerCopy(cabinets)

	return (
		<Box
			sx={{
				position: 'sticky',
				top: 'calc(var(--ImpersonationBanner-height, 0px) + var(--BetterStackBanner-height, 0px) + var(--MainNav-height))',
				zIndex: 999,
				bgcolor: 'var(--mui-palette-background-default)',
				boxShadow: isScrolled ? 'var(--mui-shadows-1)' : 'none',
				transition: 'box-shadow 200ms ease-out',
				px: 'var(--Content-paddingX)',
				pt: 2,
				pb: 2,
			}}
		>
			<Alert
				severity="error"
				data-test="active-alerts-banner"
				action={
					<Stack direction="row" spacing={1} alignItems="center">
						<Button
							color="inherit"
							component={RouterLink}
							endIcon={<ArrowRightIcon />}
							size="small"
							variant="text"
							to={
								cabinets.length === 1
									? `${healthTabHref('overview')}?cabinet=${cabinets[0].id}`
									: healthTabHref('overview')
							}
							data-test="active-alerts-banner-link"
						>
							{linkLabel}
						</Button>
						<IconButton
							aria-label="close"
							color="inherit"
							size="small"
							onClick={handleDismiss}
							data-test="active-alerts-banner-dismiss"
						>
							<XIcon />
						</IconButton>
					</Stack>
				}
			>
				<b>{message}</b> and unable to accept purchases.
			</Alert>
		</Box>
	)
}
